import { tokenStorage, type StoredAuth } from '@/platform/auth-runtime/token-storage';
import { clock } from '@/shared/lib/clock/clock';
import { WIRE_HEADERS } from '@/shared/lib/http/wire-headers';
import sha256 from 'crypto-js/sha256';

// Signs every authenticated request with a SHA-256 digest of
// (ubid + token + nonce + timestamp + body).  Backend validates the digest
// against the same fields read from headers.  No token / ubid in storage
// means the user isn't logged in → return null (axios interceptor skips
// adding the headers).
//
// Implementation note: `crypto.randomUUID()` produces the expected RFC 4122
// string and
// `crypto.subtle.digest('SHA-256', ...)` runs natively (faster + no JS bundle
// cost).  Trade-off: SubtleCrypto is async so this fn is async, and the
// axios request interceptor that consumes it is async too.
//
// Notes for behavior fidelity (don't "fix" these):
// - Nonce is the bare UUID v4 string (RFC 4122, with hyphens).
// - Timestamp is `clock.now()` — server-aligned via services/clock.ts,
//   which is synced at boot from POST /time (see services/time-sync.ts).

// Mapped via WIRE_HEADERS so the type-side names follow the constant; if
// backend renames `x-sr-signature` (etc.), both the constant and this
// shape update in one place.
export type RequestTicketHeaders = {
  [WIRE_HEADERS.nonce]: string;
  [WIRE_HEADERS.token]: string;
  [WIRE_HEADERS.timestamp]: string;
  [WIRE_HEADERS.ubid]: string;
  [WIRE_HEADERS.signature]: string;
};

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
}

export const genRequestTicket = async (bodyStr: string): Promise<RequestTicketHeaders | null> => {
  const auth = tokenStorage.read();
  if (!auth) return null;
  const { token, ubid } = auth;
  const nonce = crypto.randomUUID();
  const ts = clock.now();
  const signedString = ubid + token + nonce + ts + bodyStr;
  return {
    [WIRE_HEADERS.nonce]: nonce,
    [WIRE_HEADERS.token]: token,
    [WIRE_HEADERS.timestamp]: String(ts),
    [WIRE_HEADERS.ubid]: ubid,
    [WIRE_HEADERS.signature]: await sha256Hex(signedString),
  };
};

// Sync signer for paths that can't await SubtleCrypto — page-unload
// telemetry flush in particular, where the browser kills pending
// microtasks before fetch goes out. Uses crypto-js sha256 (sync JS impl)
// so the signed headers are ready before the fetch initiates. Returns
// `{}` when the session isn't authed (no token) so the caller can still
// fire-and-forget; backend will 401 the unsigned attempt, same as it
// would for a bare beacon. `auth` lets the caller sign with credentials it
// kept from before a sign-out cleared storage.
export function signLogBody(
  bodyStr: string,
  auth: StoredAuth | null = tokenStorage.read(),
): Record<string, string> {
  if (!auth) return {};
  const { token, ubid } = auth;
  const nonce = crypto.randomUUID();
  const ts = clock.now();
  return {
    [WIRE_HEADERS.nonce]: nonce,
    [WIRE_HEADERS.token]: token,
    [WIRE_HEADERS.timestamp]: String(ts),
    [WIRE_HEADERS.ubid]: ubid,
    [WIRE_HEADERS.signature]: sha256(ubid + token + nonce + ts + bodyStr).toString(),
  };
}
